import { DefaultInspirationPerPage } from "@/types/inspiration"
import { InitPublicProfileProps } from "@/types/profile"
import { PublicUser } from "@/types/user"
import { createAsyncThunk } from "@reduxjs/toolkit"

import { sendAnalyticsEvent } from "@/lib/analytics/amplitude-utils"
import { buildClientAxios } from "@/lib/client-axios"
import { CollectionsApi } from "@/lib/collections-api"
import { httpIsCancelled } from "@/lib/exception-utils"
import { InspirationApi } from "@/lib/inspiration-api"
import {
  addPublicCollections,
  addPublicGuides,
  resetPublicProfile,
  selectPublicCurrCollectionsPage,
  selectPublicCurrInspirationPage,
  selectPublicLoadingCollections,
  selectPublicLoadingGuides,
  selectPublicMaxCollectionsPage,
  selectPublicMaxInspirationPage,
  selectPublicTab,
  selectPublicUser,
  setPublicCollections,
  setPublicCollectionsCount,
  setPublicCurrCollectionsPage,
  setPublicCurrInspirationPage,
  setPublicGuides,
  setPublicGuidesCount,
  setPublicLoadingCollections,
  setPublicLoadingGuides,
  setPublicMaxCollectionsPage,
  setPublicMaxInspirationPage,
  setPublicTab,
  setPublicUser,
  setPublicUserProfile,
} from "@/lib/storage/public-profile-slice"
import { AppDispatch, RootState } from "@/lib/storage/redux-store"
import userSlice, { selectFollowStatus } from "@/lib/storage/user-slice"
import { toastManager } from "@/lib/toast"
import { isFollowingStatus, isSearchable } from "../user-utils"
import { PublicProfileTabType } from "./public-profile-slice"

const inspirationApi = new InspirationApi(buildClientAxios())
const collectionsApi = new CollectionsApi(buildClientAxios())

let fetchInspirationAbort: { abort: () => void } | undefined = undefined
export const fetchPublicUserInspirations = createAsyncThunk<
  void,
  { page: number; user: PublicUser },
  { dispatch: AppDispatch; state: RootState }
>(
  "publicProfile/fetchPublicUserInspirations",
  async ({ page, user }, { dispatch, abort, signal }) => {
    fetchInspirationAbort?.abort()
    fetchInspirationAbort = { abort }
    try {
      dispatch(setPublicLoadingGuides(true))
      const inspirationsRet = await inspirationApi.fetchPublicUserInspirations(
        {
          handle: user.handle,
          page: page,
          perPage: DefaultInspirationPerPage,
        },
        { signal }
      )

      if (page === 1) {
        dispatch(setPublicGuides(inspirationsRet.data))
      } else {
        dispatch(addPublicGuides(inspirationsRet.data))
      }

      dispatch(setPublicCurrInspirationPage(inspirationsRet.meta.current_page))
      dispatch(setPublicMaxInspirationPage(inspirationsRet.meta.total_pages))
    } catch (e) {
      if (!httpIsCancelled(e)) {
        console.error(e)
        toastManager.add({
          type: "error",
          description: "Failed to fetch guides",
        })
      }
    } finally {
      dispatch(setPublicLoadingGuides(false))
    }
  }
)

export const loadPublicGuides = createAsyncThunk<
  void,
  { user: PublicUser },
  { dispatch: AppDispatch; state: RootState }
>("publicProfile/loadGuides", async ({ user }, { dispatch }) => {
  try {
    dispatch(setPublicLoadingGuides(true))
    await dispatch(fetchPublicUserInspirations({ page: 1, user }))
  } finally {
    dispatch(setPublicLoadingGuides(false))
  }
})

export const handlePublicUserGuidesNext = createAsyncThunk<
  void,
  { user: PublicUser },
  { dispatch: AppDispatch; state: RootState }
>(
  "publicProfile/handlePublicUserGuidesNext",
  async ({ user }, { dispatch, getState }) => {
    const state = getState()
    const currPage = selectPublicCurrInspirationPage(state)
    const maxPage = selectPublicMaxInspirationPage(state)
    const isLoading = selectPublicLoadingGuides(state)

    if (isLoading || currPage >= maxPage) return

    const newPage = currPage + 1
    await dispatch(fetchPublicUserInspirations({ page: newPage, user }))
  }
)

export const loadPublicCollections = createAsyncThunk<
  void,
  PublicUser,
  { dispatch: AppDispatch; state: RootState }
>("publicProfile/loadPublicCollections", async (user, { dispatch }) => {
  try {
    dispatch(setPublicLoadingCollections(true))
    await dispatch(fetchPublicUserCollections({ page: 1, user }))
  } finally {
    dispatch(setPublicLoadingCollections(false))
  }
})

let fetchPublicUserCollectionsAbort: { abort: () => void } | undefined =
  undefined

export const fetchPublicUserCollections = createAsyncThunk<
  void,
  { page: number; user: PublicUser },
  { dispatch: AppDispatch }
>(
  "publicProfile/fetchPublicUserCollections",
  async ({ page, user }, { dispatch, abort }) => {
    fetchPublicUserCollectionsAbort?.abort()
    fetchPublicUserCollectionsAbort = { abort }
    try {
      dispatch(setPublicLoadingCollections(true))

      if (!user || !user.handle) {
        return
      }

      const response = await collectionsApi.fetchPublicUserCollections({
        handle: user.handle,
        page,
      })

      if (page === 1) {
        dispatch(setPublicCollections(response.data))
      } else {
        dispatch(addPublicCollections(response.data))
      }

      dispatch(setPublicCurrCollectionsPage(response.meta.current_page))
      dispatch(setPublicMaxCollectionsPage(response.meta.total_pages))
    } catch (e) {
      if (!httpIsCancelled(e)) {
        console.error("Failed to fetch public user collections", e)
        toastManager.add({
          type: "error",
          description: "Sorry, we couldn't load this user's collections.",
        })
      }
    } finally {
      dispatch(setPublicLoadingCollections(false))
    }
  }
)

export const setPublicTabWithData = createAsyncThunk<
  void,
  { tab: PublicProfileTabType; user: PublicUser },
  { dispatch: AppDispatch; state: RootState }
>(
  "publicProfile/setPublicTabWithData",
  async ({ tab, user }, { dispatch, getState }) => {
    const state = getState()
    const currentTab = selectPublicTab(state)
    const followStatus = selectFollowStatus(state, user.user_ref)
    const acceptedFollower = isFollowingStatus(followStatus)
    const canSeeData = (user && isSearchable(user)) || acceptedFollower

    // Only fetch if changing tabs or no data
    if (
      tab !== currentTab ||
      (tab === "guides" &&
        state.publicProfileSlice.publicGuides.length === 0) ||
      (tab === "collections" &&
        state.publicProfileSlice.publicCollections.length === 0)
    ) {
      dispatch(setPublicTab(tab))

      if (!canSeeData) return

      if (tab === "collections") {
        await dispatch(loadPublicCollections(user))
      } else if (tab === "reviews") {
        // Reviews are loaded by RTK Query in the tab component.
      } else {
        await dispatch(loadPublicGuides({ user }))
      }
    }
  }
)

export const initPublicProfile = createAsyncThunk<
  void,
  InitPublicProfileProps,
  { dispatch: AppDispatch; state: RootState }
>(
  "publicProfile/initPublicProfile",
  async (initProps, { dispatch, getState }) => {
    const currentUser = selectPublicUser(getState())
    if (currentUser && currentUser.handle !== initProps.user.handle) {
      dispatch(resetPublicProfile())
    }

    dispatch(setPublicUser(initProps.user))
    dispatch(setPublicUserProfile(initProps.userProfile))
    dispatch(setPublicGuidesCount(initProps.guidesCount))
    dispatch(setPublicCollectionsCount(initProps.collectionsCount))

    dispatch(
      userSlice.actions.setFollowCounts({
        userRef: initProps.user.user_ref,
        followers: initProps.userProfile.accepted_followers_count || 0,
        following: initProps.userProfile.following_count || 0,
      })
    )

    const currentTab = selectPublicTab(getState())

    await dispatch(
      setPublicTabWithData({
        tab: currentTab,
        user: initProps.user,
      })
    )

    sendAnalyticsEvent("ViewPublicProfilePage")
  }
)

export const handlePublicUserCollectionsNext = createAsyncThunk<
  void,
  PublicUser,
  { dispatch: AppDispatch; state: RootState }
>(
  "publicProfile/handlePublicUserCollectionsNext",
  async (user, { dispatch, getState }) => {
    const state = getState()
    const currPage = selectPublicCurrCollectionsPage(state)
    const maxPage = selectPublicMaxCollectionsPage(state)
    const isLoading = selectPublicLoadingCollections(state)

    if (isLoading || currPage >= maxPage) return

    const newPage = Math.min(maxPage, currPage + 1)
    await dispatch(fetchPublicUserCollections({ page: newPage, user }))
  }
)
